import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'
import { convertRawValue } from '@/utils/CustomEnum'
import { buildCookie } from '@/modules/cdn/cookie'

import { ApiTaCm020 } from '~/endpoints/ApiTaCm020'
import { ApiTaCt100 } from '~/endpoints/ApiTaCt100'
import { ApiTaCt020 } from '~/endpoints/ApiTaCt020'
import { ApiTaCt030 } from '~/endpoints/ApiTaCt030'
import { ApiTaPt060 } from '~/endpoints/ApiTaPt060'

export namespace ViewerType {
  /**
   * ApiTaCt020: コンテンツ取得
   */
  export const studiedFlgType = {
    UNDONE: 0, // 未取組
    DONE: 1, // 取組済
  } as const
  export type StudiedFlgType = typeof studiedFlgType[keyof typeof studiedFlgType]

  export const pieceLvType = {
    NORMAL: 0, // 通常
    STANDARD: 1, // あなた専用学習（スタンダード）
    HIGH: 2, // あなた専用学習（ハイレベル）
  } as const
  export type PieceLvType = typeof pieceLvType[keyof typeof pieceLvType]

  export const contentTypeType = {
    START: '0', // スタート画面
    READING: '1', // 読み物
    AUTO_SCORING: '2', // 自動採点
    SELF_SCORING: '3', // 自己採点
    SUB_PROBLEM: '4', // 枝問含む
    KANJI_KAKITORI: '5', // 漢字書取コンテンツ
    KANJI_MONDAI: '6', // 漢字問題コンテンツ
  } as const
  export type ContentTypeType = typeof contentTypeType[keyof typeof contentTypeType]

  export const statusType = {
    WRONG: '0', // x
    OK: '1', // o
    PARTIAL_OK: '2', // △
    SKIP: '9', // -
  } as const
  export type StatusType = typeof statusType[keyof typeof statusType]

  export const contentCategoryType = {
    POINT: 1, // 要点学習
    CONFIRMATION: 2, // 確認問題
    PRACTICE: 3, // 練習問題
    OTHER: 4, // その他
  } as const
  export type ContentCategoryType = typeof contentCategoryType[keyof typeof contentCategoryType]

  /**
   * ApiTaCt030: 学習結果登録
   */
  export const answerRightType = {
    WRONG: '0', // x
    OK: '1', // o
    PARTIAL_OK: '2', // △
    SKIP: '9', // -
  } as const
  export type AnswerRightType = typeof answerRightType[keyof typeof answerRightType]
}

// State types

interface ServerTimeState {
  serverDate: string // yyyy/MM/dd
  serverTime: string // hh:mm:ss
  serverDateAndTime: string // yyyy-MM-ddThh:mm:ss+hh:MM
}
const getEmptyServerTimeState = (): ServerTimeState => ({
  serverDate: '',
  serverTime: '',
  serverDateAndTime: '',
})

interface AkamaiTokenState {
  akamaiToken: string // Akamaiトークン
}
const getEmptyAkamaiTokenState = (): AkamaiTokenState => ({
  akamaiToken: '',
})

export interface ContentNode {
  contentId: string // 学習コンテンツID
  name: string // コンテンツ名称
  questionNo: number // コンテンツ問題番号
  contentUrl: string // コンテンツURL
  contentType: ViewerType.ContentTypeType // コンテンツ分類
  sortNo: number // 表示順
  point: number // 配点
  status: ViewerType.StatusType // 正誤判定結果
  isLastQuestion: boolean // 最終問題フラグ（0:最終問題ではない, 1:最終問題である）
  contentCategory: ViewerType.ContentCategoryType // コンテンツカテゴリ
}
export interface ContentsState {
  deliveryYear: number // 学習年
  deliveryMonth: number // 学習月
  productId: string // 商品ID
  nameDisp: string // 学習コマ名称
  pieceTypeId: string // 学習コマ区分
  pieceStudiedFlg: ViewerType.StudiedFlgType // 学習コマ取組みフラグ
  pieceLv: ViewerType.PieceLvType // 学習コマレベル
  contents: ContentNode[] // 学習コンテンツ
}
const getEmptyContentsState = (): ContentsState => ({
  deliveryYear: 0,
  deliveryMonth: 0,
  productId: '',
  nameDisp: '',
  pieceTypeId: '',
  pieceStudiedFlg: 0,
  pieceLv: 0,
  contents: [],
})

interface PuchipoStatusState {
  showPuchipoPointModal: boolean // ぷちぽ獲得モーダル表示フラグ
  puchipoNum: number // ぷちぽ数
}
const getEmptyPuchipoStatusState = (): PuchipoStatusState => ({
  showPuchipoPointModal: false,
  puchipoNum: 0,
})

interface PuchipoMessageState {
  puchipoMessage: string // ぷちぽ獲得メッセージ
}
const getEmptyPuchipoMessageState = (): PuchipoMessageState => ({
  puchipoMessage: '',
})

// State action parameter

interface ActionParamsFetchContents {
  pieceId: string // 学習コマID
  unitId: string // 配信単位ID
  contentId?: string[] // 学習コンテンツID
}

export interface StudyTimeNode {
  startTime: string // 開始時間（yyyy/MM/dd hh:mm:ss）
  studyTime: string // 学習時間（mm:ss）
}
export interface PieceNode {
  reLearning: number // 解き直し回数
  contentId: string // 学習コンテンツID
  questionNum: number // 問題番号（大問の場合は0で、大問に紐づく枝問の場合は1始まりのシーケンス番号）
  answerRight: ViewerType.AnswerRightType // 正誤判定結果
  userAnswer: string // ユーザ回答内容
  studyTimes: StudyTimeNode[] // 学習時間リスト
}
export interface ActionParamsRegisterStudyResult {
  courseId: string // コースID
  planId: string // プランID
  unitId: string // 配信単位ID
  pieceId: string // 学習コマID
  piece: PieceNode[] // 学習コマコンテンツ
}

interface ActionParamsFetchPuchipoMessage {
  getPuchipoPoint: number // 獲得ぷちぽ数
}

const MATOME_PIECE_CATEGORY_VALUE = '7'
const KANJI_KAKITORI_TYPE = '21'
const KANJI_MONDAI_TYPE = '22'

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/Viewer',
})
export default class Viewer extends VuexModule {
  // State

  private serverTime_ = getEmptyServerTimeState()

  private akamaiToken_ = getEmptyAkamaiTokenState()

  private contents_ = getEmptyContentsState()

  private puchipoStatus_ = getEmptyPuchipoStatusState()

  private puchipoMessage_ = getEmptyPuchipoMessageState()

  // Getters

  get serverTime(): ServerTimeState {
    return this.serverTime_
  }

  get akamaiCookie(): (expiresAt?: string) => string {
    return (expiresAt?: string) => buildCookie(this.akamaiToken_.akamaiToken, expiresAt)
  }

  get contents(): ContentsState {
    return this.contents_
  }

  get isFirstAttempt(): boolean {
    return this.contents_.pieceStudiedFlg === ViewerType.studiedFlgType.UNDONE
  }

  get isMatomePiece(): boolean {
    return this.contents_.pieceTypeId === MATOME_PIECE_CATEGORY_VALUE
  }

  get isKanjiKakitoriPiece(): boolean {
    return this.contents_.pieceTypeId === KANJI_KAKITORI_TYPE
  }

  get isKanjiDrillPiece(): boolean {
    return this.contents_.pieceTypeId === KANJI_KAKITORI_TYPE || this.contents_.pieceTypeId === KANJI_MONDAI_TYPE
  }

  get puchipoStatus(): PuchipoStatusState {
    return this.puchipoStatus_
  }

  get puchipoMessage(): PuchipoMessageState {
    return this.puchipoMessage_
  }

  // Mutations

  @Mutation
  updateServerTime(value: ServerTimeState) {
    this.serverTime_ = value
  }

  @Mutation
  updateAkamaiToken(value: AkamaiTokenState) {
    this.akamaiToken_ = value
  }

  @Mutation
  updateContents(value: ContentsState) {
    this.contents_ = value
  }

  @Mutation
  updatePuchipoStatus(value: PuchipoStatusState) {
    this.puchipoStatus_ = value
  }

  @Mutation
  updatePuchipoMessage(value: PuchipoMessageState) {
    this.puchipoMessage_ = value
  }

  @Action
  clear() {
    this.context.commit('updateServerTime', getEmptyServerTimeState())
    this.context.commit('updateAkamaiToken', getEmptyAkamaiTokenState())
    this.context.commit('updateContents', getEmptyContentsState())
    this.context.commit('updatePuchipoStatus', getEmptyPuchipoStatusState())
    this.context.commit('updatePuchipoMessage', getEmptyPuchipoMessageState())
  }

  @Action
  async fetchServerTime() {
    logger.trace('in: fetchServerTime')

    // API: API_TA_CM_020
    const api = new ApiTaCm020.Api()
    const result = await api.get<ServerTimeState | null>(
      { query: {} },
      // success
      (data, _) => {
        return {
          serverDate: data.serverDate,
          serverTime: data.serverTime,
          serverDateAndTime: data.serverDateAndTime,
        }
      },
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return
    }
    this.context.commit('updateServerTime', result)
  }

  @Action
  async fetchAkamaiToken() {
    logger.trace('in: fetchAkamaiToken')

    // API: API_TA_CT_100
    const api = new ApiTaCt100.Api()
    const result = await api.get<AkamaiTokenState | null>(
      { query: {} },
      // success
      (data, _) => {
        return {
          akamaiToken: data.cmsToken,
        }
      },
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return
    }
    this.context.commit('updateAkamaiToken', result)
  }

  @Action
  async fetchContents(param: ActionParamsFetchContents) {
    logger.trace('in: fetchContents')

    // API: API_TA_CT_020
    const api = new ApiTaCt020.Api()
    const result = await api.get<ContentsState | null>(
      {
        query: {
          pieceId: param.pieceId,
          unitId: param.unitId,
          contentId: param.contentId,
        },
      },
      // success
      (data, _) => {
        return {
          deliveryYear: data.deliveryYear,
          deliveryMonth: data.deliveryMonth,
          productId: data.productId,
          nameDisp: data.nameDisp,
          pieceTypeId: data.pieceTypeId,
          pieceStudiedFlg: convertRawValue<number, ViewerType.StudiedFlgType>(
            data.pieceStudiedFlg,
            ViewerType.studiedFlgType
          ),
          pieceLv: convertRawValue<number, ViewerType.PieceLvType>(data.pieceLv, ViewerType.pieceLvType),
          contents: ((response: ApiTaCt020.Get.Response) =>
            response.contents.map(c => ({
              contentId: c.contentId,
              name: c.name,
              questionNo: c.questionNo,
              contentUrl: c.contentUrl,
              contentType: convertRawValue<string, ViewerType.ContentTypeType>(
                c.contentType,
                ViewerType.contentTypeType
              ),
              sortNo: c.sortNo,
              point: c.point,
              status: convertRawValue<string, ViewerType.StatusType>(c.status, ViewerType.statusType),
              isLastQuestion: c.isLastQuestion,
              contentCategory: convertRawValue<number, ViewerType.ContentCategoryType>(
                c.contentCategory,
                ViewerType.contentCategoryType
              ),
            })))(data),
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return
    }
    this.context.commit('updateContents', result)
  }

  @Action
  async registerStudyResult(param: ActionParamsRegisterStudyResult): Promise<Error | undefined> {
    logger.trace('in: registerStudyResult')

    // API: API_TA_CT_030
    const api = new ApiTaCt030.Api()
    const result = await api.post<PuchipoStatusState | null>(
      {
        body: {
          courseId: param.courseId,
          planId: param.planId,
          unitId: param.unitId,
          pieceId: param.pieceId,
          piece: param.piece,
        },
      },
      (data, _) => {
        return {
          showPuchipoPointModal: data.showPuchipoPointModal,
          puchipoNum: data.puchipoNum,
        }
      },
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return new Error('Failed to register study result')
    }
    this.context.commit('updatePuchipoStatus', result)
  }

  @Action
  async fetchPuchipoMessage(param: ActionParamsFetchPuchipoMessage) {
    logger.trace('in: fetchPuchipoMessage')

    // API: API_TA_PT_060
    const api = new ApiTaPt060.Api()
    const result = await api.get<PuchipoMessageState | null>(
      {
        query: {
          getPuchipoPoint: param.getPuchipoPoint,
        },
      },
      // success
      (data, _) => {
        return {
          puchipoMessage: data.puchipoMessage,
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return
    }
    this.context.commit('updatePuchipoMessage', result)
  }
}
